import React, { useMemo } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Stack,
  Typography,
} from "@mui/material";
import { getRoleMeta, groupRoles } from "./roleDisplay";
import UserAvatar from "../../components/common/UserAvatar";

const getUserDisplayName = (user) => {
  const first = user?.firstName || user?.firstname || "";
  const last = user?.lastName || user?.lastname || "";
  const full = `${first} ${last}`.trim();
  return full || user?.userName || user?.email || "Unknown user";
};

const isUserActive = (user) => Number(user?.active) === 1;

const ViewRoleUserDialog = ({ open, onClose, user, loading, error }) => {
  const displayName = getUserDisplayName(user);
  const active = isUserActive(user);
  const roleGroups = useMemo(
    () => groupRoles((user?.roles || []).map((name) => ({ name }))),
    [user]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="view-role-user-title"
    >
      <DialogTitle id="view-role-user-title" sx={{ pr: 3 }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <UserAvatar
            profilePhotoFileId={user?.profilePhotoFileId}
            displayName={displayName}
            size={48}
          />
          <Box minWidth={0}>
            <Typography variant="h6" noWrap>
              {displayName}
            </Typography>
            <Chip
              size="small"
              label={active ? "Active" : "Inactive"}
              color={active ? "success" : "default"}
              sx={{ height: 20, fontSize: 11, fontWeight: 700 }}
            />
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress aria-label="Loading user details" />
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {error ? <Alert severity="warning">{error}</Alert> : null}
            {roleGroups.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                This user is not in any groups.
              </Typography>
            ) : (
              roleGroups.map((section) => (
                <Box key={section.group}>
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 700, color: section.color, display: "block", mb: 0.5 }}
                  >
                    {section.group}
                  </Typography>
                  <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                    {section.roles.map((role) => {
                      const meta = role.meta || getRoleMeta(role.name);
                      return (
                        <Chip
                          key={role.name}
                          size="small"
                          label={`${meta.code}  ${meta.title}`}
                          sx={{
                            fontWeight: 600,
                            color: section.color,
                            borderColor: section.color,
                          }}
                          variant="outlined"
                        />
                      );
                    })}
                  </Stack>
                </Box>
              ))
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} aria-label="Close user details">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewRoleUserDialog;
